export async function getBgColorFromFile(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (event) {
      const img = new Image();
      img.onload = function () {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Could not get canvas context');
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const pixelData = ctx.getImageData(0, 0, img.width, img.height).data;
        const bgColor = getBackgroundColor(pixelData, img.width, img.height);
        resolve(bgColor);
      };
      img.src = event.target?.result as string;
    };

    function sampleEdgePixels(
      pixelData: Uint8ClampedArray,
      width: number,
      height: number,
      sampleSize: number,
    ): string[] {
      const edgePixels: string[] = [];

      const sampleEdge = (start: number, end: number, getPixel: (i: number) => string) => {
        const step = Math.max(Math.floor((end - start) / sampleSize), 1);
        for (let i = start; i < end; i += step) {
          const color = getPixel(i);

          if (color) {
            edgePixels.push(color);
          }
        }
      };

      sampleEdge(0, width, (x) => getPixelColor(pixelData, x, 0, width));
      sampleEdge(0, width, (x) => getPixelColor(pixelData, x, height - 1, width));
      sampleEdge(0, height, (y) => getPixelColor(pixelData, 0, y, width));
      sampleEdge(0, height, (y) => getPixelColor(pixelData, width - 1, y, width));

      return edgePixels;
    }

    function countColorFrequency(colors: string[]): Record<string, number> {
      return colors.reduce(
        (acc, color) => {
          acc[color] = (acc[color] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );
    }

    function findMostFrequentColor(colorCount: Record<string, number>): string {
      return Object.entries(colorCount).reduce(
        (max, [color, count]) => (count > max[1] ? [color, count] : max),
        ['', 0],
      )[0];
    }

    function getBackgroundColor(
      pixelData: Uint8ClampedArray,
      width: number,
      height: number,
    ): string {
      const sampleSize = 30;
      const edgePixels = sampleEdgePixels(pixelData, width, height, sampleSize);
      const colorCount = countColorFrequency(edgePixels);
      return findMostFrequentColor(colorCount);
    }

    function getPixelColor(
      pixelData: Uint8ClampedArray,
      x: number,
      y: number,
      width: number,
    ): string {
      const index = (y * width + x) * 4;
      const r = pixelData[index];
      const g = pixelData[index + 1];
      const b = pixelData[index + 2];
      const alpha = pixelData[index + 3];

      if (alpha === 0) {
        return ''; // Ignore transparent pixels
      }

      return rgbToHex(r, g, b);
    }

    function rgbToHex(r: number, g: number, b: number): string {
      return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
    }

    function componentToHex(c: number): string {
      const hex = c.toString(16);
      return hex.length == 1 ? '0' + hex : hex;
    }
    reader.onerror = function () {
      reject('Error reading file');
    };

    const validFormats = ['image/jpeg', 'image/png', 'image/svg+xml'];
    if (validFormats.includes(file.type)) {
      reader.readAsDataURL(file);
    } else {
      // Resolve white if unsupported file type
      resolve('#ffffff');
    }
  });
}
